import React, { FC } from 'react';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import SVG from '@app/components/SVG';
import DocumentSpriteSymbol from '@app/images/document-icon.svg';
import TrashSpriteSymbol from '@app/images/trash-black.svg';
import { FileAndErrors } from '@app/domain/files';
import { SupplierDocumentType } from '@supplierRegistration/stores/appStores/SupplierRegistrationDocumentsStore';
import {
  DeleteButton,
  FileRow,
  FileRowErrorText,
  FileRowIcon,
  FileRowInfo,
  FileRowName,
  FileRowNameLine,
  FileRowStatus,
} from '@supplierRegistration/routes/supplierRegistrationProcess/documentUpload/DocumentUploadSection.styles';

export interface DocumentFileRowProps {
  type: SupplierDocumentType;
  index: number;
  fileAndErrors: FileAndErrors;
  onDelete: (index: number) => void;
}

// index is this file's current position in filesByType[type] -- callers must derive it from
// render position (map index), not cache it, since it shifts after every delete.
// observer is load-bearing: server 422 errors and cap recalculation land as in-place
// `entry.errors` assignments (no array push/splice), which only this component reads.
const DocumentFileRow: FC<DocumentFileRowProps> = observer((props) => {
  const { type, index, fileAndErrors, onDelete } = props;
  const { t } = useTranslation();
  const { file, errors } = fileAndErrors;

  const rowTestId = `supp-reg-document-file-row-${type}-${index}`;
  const deleteTestId = `btn-supp-reg-document-file-delete-${type}-${index}`;
  const deleteLabel = t('supplierValidation.supplierRegister.documents.delete');

  return (
    <FileRow data-testid={rowTestId}>
      <FileRowIcon>
        <SVG accessibilityLabel='' image={DocumentSpriteSymbol} width={14} height={17} />
      </FileRowIcon>
      <FileRowInfo>
        <FileRowNameLine>
          <FileRowName>{file.name}</FileRowName>
          <FileRowStatus>{t('supplierValidation.supplierRegister.documents.attached')}</FileRowStatus>
        </FileRowNameLine>
        {errors?.map((errorKey) => (
          <FileRowErrorText key={errorKey}>{t(errorKey)}</FileRowErrorText>
        ))}
      </FileRowInfo>
      <DeleteButton
        id={deleteTestId}
        dataTestId={deleteTestId}
        appearance='text'
        colorScheme='red'
        aria-label={`${deleteLabel} ${file.name}`}
        onClick={(): void => onDelete(index)}
      >
        <SVG accessibilityLabel='' image={TrashSpriteSymbol} width={12} height={12} />
        {deleteLabel}
      </DeleteButton>
    </FileRow>
  );
});

export default DocumentFileRow;
